import { Classifier } from '../../types'

/**
 * Classifier details — a ClassifierDetails in EN-258: not versioned, saves in
 * place, and the version rail never touches it. Deliberately plain, so the
 * accented prompt-version card next to it reads as the versioned half.
 */
export default function ClassifierDetailsCard({ classifier }: { classifier: Classifier }) {
    return (
        <div className="bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span className="font-semibold text-sm text-gray-700">Classifier details</span>
                <span className="text-[11px] font-normal text-gray-400">
                    Applies to every version
                </span>
            </div>
            <div className="p-4 space-y-4">
                {/* Title */}
                <div>
                    <label
                        htmlFor="classifier-title"
                        className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1"
                    >
                        Title{' '}
                        <span className="normal-case text-gray-400 font-normal">
                            — internal, shown in the library
                        </span>
                    </label>
                    <input
                        id="classifier-title"
                        type="text"
                        defaultValue={classifier.title}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-sans"
                    />
                </div>

                {/* Client-facing name */}
                <div>
                    <label
                        htmlFor="classifier-client-name"
                        className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1"
                    >
                        Client-facing name{' '}
                        <span className="normal-case text-gray-400 font-normal">
                            — shown to clients
                        </span>
                    </label>
                    <input
                        id="classifier-client-name"
                        type="text"
                        defaultValue={classifier.clientFacingName}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-sans"
                    />
                </div>

                {/* Description */}
                <div>
                    <label
                        htmlFor="classifier-description"
                        className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1"
                    >
                        Description{' '}
                        <span className="normal-case text-gray-400 font-normal">
                            — shown to clients
                        </span>
                    </label>
                    <textarea
                        id="classifier-description"
                        defaultValue={classifier.description}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 font-sans"
                    />
                </div>

                {/* Modality segmented control (static) */}
                <div>
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
                        Modality
                    </span>
                    <div
                        className="inline-flex border border-gray-300 rounded-lg overflow-hidden"
                        role="group"
                        aria-label="Modality"
                    >
                        <span
                            className={`px-3 py-2 text-xs font-semibold ${classifier.modality === 'text' ? 'bg-arwen-600 text-white' : 'text-gray-500'}`}
                        >
                            Text
                        </span>
                        <span
                            className={`px-3 py-2 text-xs border-l border-gray-200 ${classifier.modality === 'image' ? 'font-semibold bg-arwen-600 text-white' : 'text-gray-500'}`}
                        >
                            Image
                        </span>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-100">
                <span className="mr-auto text-[11px] text-gray-400">
                    Changes here save immediately — they do not create a new version.
                </span>
                <button
                    type="button"
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                    Save details
                </button>
            </div>
        </div>
    )
}
